import { runMarketingTrackingEvent } from './thirdPartyTracking';

/**
 * TikTok Pixel helper — thin wrapper around the global ttq object.
 * Only fires on production hosts to keep test/preview traffic out of real pixel data.
 *
 * Pixel ID: DA7LF8JC77UC8FLJ7ETG
 *
 * Funnel event map:
 *   Landing page load  → ViewContent + PageView (auto)
 *   CTA click          → ClickButton
 *   Email confirmed    → CompleteRegistration
 *   Stripe opens       → InitiateCheckout
 *   Stripe succeeds    → Subscribe
 */

const PRODUCTION_HOSTS = ['lovibook.com', 'www.lovibook.com'];

function isProductionHost() {
  return typeof window !== 'undefined' && PRODUCTION_HOSTS.includes(window.location.hostname);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getTtq(): any | null {
  if (!isProductionHost()) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ttq = (window as any).ttq;
  return ttq && typeof ttq === 'object' ? ttq : null;
}

/** Fire on every client-side route change. */
export function trackPageView() {
  return runMarketingTrackingEvent(() => getTtq()?.page());
}

/** Fire on landing page load — explicit high-intent signal. */
export function trackViewContent(contentName: string) {
  return runMarketingTrackingEvent(() => getTtq()?.track('ViewContent', { content_name: contentName }));
}

/** Fire when a visitor clicks a sign-up CTA — signals purchase intent. */
export function trackLead() {
  return runMarketingTrackingEvent(() => getTtq()?.track('ClickButton'));
}

/** Fire when the user confirms their email. */
export function trackCompleteRegistration() {
  return runMarketingTrackingEvent(() => getTtq()?.track('CompleteRegistration'));
}

/** Fire when the user enters Stripe checkout. */
export function trackInitiateCheckout(planName: string, planPrice: number) {
  return runMarketingTrackingEvent(() => getTtq()?.track('InitiateCheckout', {
    content_name: planName,
    currency: 'USD',
    value: planPrice,
  }));
}

/** Fire after Stripe subscription payment succeeds. */
export function trackSubscribe(planName: string, planPrice: number, eventId?: string) {
  return runMarketingTrackingEvent(() => getTtq()?.track('Subscribe', {
    content_name: planName,
    currency: 'USD',
    value: planPrice,
  }, eventId ? { event_id: eventId } : undefined));
}
